/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import { useNavigate } from 'react-router-dom'
import Button from '@mui/material/Button'
import WarningRoundedIcon from '@mui/icons-material/WarningRounded'
import DefaultProximityLogo from '../../components/1elements/Icons/svg/proximityLogo.svg'
import sixfactorLogo from '../../components/1elements/Icons/svg/sixfactorLogoMono.svg'
import { Text } from '../../components/1elements'
import { GOOGLE_CONFIG } from '../../config'
import { Messages } from '../../Messages'
import {
  responseGoogle,
  failedGoogleAuthMessage
} from './reducers/googleSignIn'

//import { MicrosoftLogin } from './reducers/microsoftSignIn'
import FakeMicrosoftButton from '../../components/1elements/Buttons/FakeMicrosoftButton/FakeMicrosoftButton'
import signInBackground from '../../assets/signInBackground.jpg'
import {
  getSignInLogo,
  getSignInText,
  getSigninBg
} from '../../common/dynamicCustomizationService'

import packageJson from '../../../package.json'
import GuestLoginEmail from './GuestLoginEmail'
import ApiService from '../../common/ApiCalls'
import LoaderBackdrop from '../../components/2compounds/LoaderBackdrop'
import './AuthenticationLayout.scss'

const proximityLogo = getSignInLogo() ?? DefaultProximityLogo
const welcomeText = getSignInText() ?? 'Mapping Business Intelligence'
const signinBg = getSigninBg()

let styledBgImg = ''
if (!signinBg) styledBgImg = signInBackground
const allowDebug = process.env.NODE_ENV !== 'production'

function SignIn () {
  const navigate = useNavigate()

  const [fail, setFail] = useState(false)
  const [showGuestLogin, setShowGuestLogin] = useState(false)
  const [guestLoginEmail, setGuestLoginEmail] = useState('')
  
  const [isLoggingIn, setIsLoggingIn] = useState(false)

  const googleLoginWrapper = async (response) => {
    setIsLoggingIn(true)
    const isSuccess = await responseGoogle(response)
    setIsLoggingIn(false)

    if (!isSuccess) {
      setFail({
        title: 'Could not find account',
        message: 'You either do not have an account, or we couldn\'t find the account on record.'
      })
    }
  }

  const toggleGuestLogin = () => {
    setShowGuestLogin(!showGuestLogin)
  }

  const handleGuestLoginEmailChange = (value) => {
    setGuestLoginEmail(value)
  }

  const handleGuestLogin = () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestLoginEmail)) {
      setFail({
        title: 'Invalid Email entered',
        message: 'Please enter a valid email address.'
      })
      return
    } 
    setIsLoggingIn(true)

    ApiService().guestLogin(guestLoginEmail, window.location.host)
      .then((resp) => {
        setIsLoggingIn(false)
        if (resp?.status === 204) {
          navigate('/login/guests/confirm')
        } else {
          setFail({
            title: 'Unexpected response',
            message: 'Please check your email. If a login email is not received, please try again later.'
          })
        }
      })
      .catch((e) => {
        setIsLoggingIn(false)
        setFail({
          title: 'Error with guest login',
          message: e.message,
        })
      })
  }

  return (
    <div
      className='authentication-root'
      style={{ backgroundImage: `url(${styledBgImg})` }}
    >
      <div className='authentication-login-container'>
        <div className='authentication-logo'>
          <img src={proximityLogo} alt='logo' />
          <div
            id='localCheat'
            type='oneLiner'
            className='authentication-tagline'
            dangerouslySetInnerHTML={{ __html: welcomeText }}
          />
        </div>

        {fail && (
          <div className='authentication-alert'>
            <div className='authentication-warning-row'>
              <WarningRoundedIcon className='authentication-warning-icon' />
              <Text type='warnMessage'>
                <strong>{ fail.title }</strong>
              </Text>
            </div>
            <Text type='warnMessage'>{ fail.message }</Text>
          </div>
        )}

        <div className='authentication-actions'>
          {isLoggingIn && (
            <LoaderBackdrop open={true} />
          )}

          {!showGuestLogin && (
            <>
              <GoogleOAuthProvider clientId={GOOGLE_CONFIG.GOOGLE_CLIENT_ID}>
                {allowDebug ? (
                  <GoogleLogin
                    clientId={GOOGLE_CONFIG.GOOGLE_CLIENT_ID}
                    text='signup_with Google'
                    onSuccess={googleLoginWrapper}
                    onError={failedGoogleAuthMessage}
                    state_cookie_domain='single_host_origin'
                    width='246px'
                    height='40px'
                    useOneTap
                  />
                ) : (
                  <GoogleLogin
                    clientId={GOOGLE_CONFIG.GOOGLE_CLIENT_ID}
                    text='signup_with Google'
                    onSuccess={googleLoginWrapper}
                    onError={failedGoogleAuthMessage}
                    state_cookie_domain='single_host_origin'
                    width='246px'
                    height='40px'
                  />
                )}
              </GoogleOAuthProvider>

              <div className='authentication-spacer' />
              <FakeMicrosoftButton />
              {/*<MicrosoftLogin /> Temporarily hidden until we can fix the Microsoft login flow see -> https://redmine.aimprosoft.com/issues/220931 */}
              <div className='authentication-spacer' />
              <Button
                className='authentication-login-button'
                variant='outlined'
                onClick={toggleGuestLogin}
              >
                Guest login
              </Button>
            </>
          )}

          {showGuestLogin && (
            <>
              <Text type="terms">
                Enter the email associated with your guest login. If the
                email exists in our system and guest access is enabled, we will
                email you a link to log in with.
              </Text>

              <GuestLoginEmail
                value={guestLoginEmail}
                onValueChange={handleGuestLoginEmailChange}
              />

              <div className='authentication-spacer' />
              <Button variant="outlined" onClick={handleGuestLogin}>
                Send Link
              </Button>
              <div className='authentication-spacer' />

              <Text type="terms">
                <a
                  className='authentication-link'
                  onClick={toggleGuestLogin}
                  rel='noopener noreferrer'
                  style={{ cursor: 'pointer' }}
                >
                  Return to account login
                </a>
              </Text>
            </>
          )}

          <div className='authentication-spacer' />
          <div className='authentication-terms'>
            <Text type='terms'>
              {Messages.read_terms}
              <a
                className='authentication-link'
                target='_blank'
                rel='noopener noreferrer'
                href='https://docs.google.com/document/d/19GAZyncRNtYLwjEwMitqNNJgqb9MObtyYi39DGJh2oc/edit?usp=sharing'
              >
                {Messages.terms_of_service}
              </a>
            </Text>
          </div>
        </div>

        <div className='authentication-footer'>
          <a
            target='_blank'
            rel='noopener noreferrer'
            href='https://sixfactor.com/'
          >
            <img
              src={sixfactorLogo}
              alt='sixfactor'
              width='60px'
              className='authentication-sixfactor-logo'
            />
          </a>
          <Text type='terms'>
            <a
              className='authentication-link'
              target='_blank'
              rel='noopener noreferrer'
              href='https://sixfactor.com/contact/'
            >
              Customer Support
            </a>
          </Text>
        </div>
      </div>

      <div className='authentication-version'>
        <Text>
          {packageJson.name} version {packageJson.version}
        </Text>
      </div>
    </div>
  )
}

export default SignIn
